import { catchError, finalize, map, takeUntil, tap } from "rxjs/operators";
import { DecisionActionRequest, DecisionActionResult } from "../../models/home";
import { UxError } from "../../models/ux-error.model";
import { FrontendLoggerService } from "../../services/frontend-logger.service";
import { HomeDataService } from "../../services/home-ia/home-data.service";
import { HomeDecisionActionUxService } from "../../services/home-ia/home-decision-action-ux.service";
import { ProblemDetailsHandler } from "../../services/home-ia/problem-details-handler.service";
import { LanguageService } from "../../services/language/language.service";
import { HomeDecisionActionEchoTracker } from "./home-decision-action-echo.tracker";
import { HomeDecisionStatePatcher } from "./home-decision-state.patcher";
import { HomeDecisionRefreshPlanner } from "./home-decision-refresh.planner";
import { HomeDecisionActionRequestFactory } from "./home-decision-action-request.factory";
import { HomeDashboardStore } from "./home.component.dashboard-store";
import { HomeStreamEventCoordinator } from "./home-stream-event.coordinator";

export class HomeDecisionActionCoordinator {
  readonly requests = new HomeDecisionActionRequestFactory();
  readonly patcher: HomeDecisionStatePatcher;
  readonly planner: HomeDecisionRefreshPlanner;
  readonly echo: HomeDecisionActionEchoTracker;
  private streamEvents?: HomeStreamEventCoordinator;

  constructor(
    private readonly store: HomeDashboardStore,
    private readonly data: HomeDataService,
    private readonly errors: ProblemDetailsHandler,
    private readonly lang: LanguageService,
    private readonly actionUx: HomeDecisionActionUxService,
    private readonly logger: FrontendLoggerService
  ) {
    this.patcher = new HomeDecisionStatePatcher(store);
    this.planner = new HomeDecisionRefreshPlanner(store, actionUx);
    this.echo = new HomeDecisionActionEchoTracker(this.planner);
  }

  attachStreamEvents(streamEvents: HomeStreamEventCoordinator): void { this.streamEvents = streamEvents; }

  execute(event: { decisionId: string; actionKey: string; request?: DecisionActionRequest }): void {
    if (!event.decisionId || !event.actionKey || this.store.actionLoadingSubject.value) {
      this.store.actionErrorSubject.next(this.buildLocalError());
      return;
    }
    const request = this.requests.build(event);
    this.clearFeedback();
    this.store.actionLoadingSubject.next(true);
    this.data.executeDecisionAction(event.decisionId, request).pipe(
      tap((result) => this.handleResult(event.decisionId, request, result)),
      catchError((error) => this.errors.handle(error).pipe(
        tap((uxError) => this.store.actionErrorSubject.next(uxError)),
        map(() => null)
      )),
      finalize(() => this.store.actionLoadingSubject.next(false)),
      takeUntil(this.store.destroy$)
    ).subscribe();
  }

  handleResult(decisionId: string, request: DecisionActionRequest, result: DecisionActionResult): void {
    if (!result.actionResultType && result.success && !result.idempotencyReplay) {
      this.logger.warn("[HomeComponent] Decision action result missing actionResultType", {
        decisionId, action: result.action, reevaluationStatus: result.reevaluationStatus, outcome: result.outcome
      });
    }
    const feedback = this.actionUx.getActionResultMessage(result);
    const refreshTargets = this.planner.resolve(decisionId, result);
    if (feedback.tone === "error") {
      this.store.actionErrorSubject.next({
        title: this.lang.translate("home.decisionDetail.actionFailedTitle"), message: feedback.message
      });
      if (refreshTargets.length) this.streamEvents?.queue(...refreshTargets);
      return;
    }
    this.store.actionMessageSubject.next(feedback.message);
    if (this.planner.shouldPatchLocally(result)) this.patcher.apply(decisionId, result);
    this.echo.remember(decisionId, request, result, refreshTargets);
    if (refreshTargets.length) this.streamEvents?.queue(...refreshTargets);
  }

  clearFeedback(): void {
    this.store.actionMessageSubject.next(null);
    this.store.actionErrorSubject.next(null);
  }
  buildLocalError(): UxError {
    return {
      title: this.lang.translate("home.decisionDetail.actionFailedTitle"),
      message: this.lang.translate("home.decisionDetail.actionUnavailable")
    };
  }
}
